import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Input } from '../design-system';
import '../styles/Auth.css';

// ─── Split-screen auth shell ─────────────────────────────────────────────────
// Left: layered mesh-gradient brand panel with floating product mockups.
// Right: one focused form. Pages own all logic; this frames it.
// Duplicated in the employee portal — keep the two copies in step.

const STROKE = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

// Feature icons referenced by key from the brand files, so those stay plain data.
const FEATURE_ICONS = {
  calendar: (
    <svg {...STROKE}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 10h17M8 2.8V6.5M16 2.8V6.5" />
      <path d="M7.5 14.2l2 2 3.4-3.7" />
    </svg>
  ),
  approve: (
    <svg {...STROKE}>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M8.2 12.4l2.5 2.5 5-5.3" />
    </svg>
  ),
  chart: (
    <svg {...STROKE}>
      <path d="M4 4v15.2a.8.8 0 0 0 .8.8H20" />
      <path d="M8.5 15.5v-4M12.7 15.5V8.2M16.9 15.5v-6" />
    </svg>
  ),
  shield: (
    <svg {...STROKE}>
      <path d="M12 3.4l7 2.7v5.2c0 4.4-2.9 7.6-7 9-4.1-1.4-7-4.6-7-9V6.1l7-2.7z" />
      <circle cx="12" cy="11" r="1.2" fill="currentColor" stroke="none" />
      <path d="M12 12.4v2.8" />
    </svg>
  ),
  tasks: (
    <svg {...STROKE}>
      <rect x="4" y="3.5" width="16" height="17" rx="2.5" />
      <path d="M8 8.5h8M8 12h8M8 15.5h4.5" />
    </svg>
  ),
  clock: (
    <svg {...STROKE}>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 7.4V12l3.2 2" />
    </svg>
  ),
};

// Firm / Employee segmented toggle shown at the top of the auth forms. It lets
// the visitor say which kind of account they're using; the page decides what
// that means (Login tailors its copy, Register swaps firm sign-up for the
// invite-only employee notice).
export function AuthTypeToggle({ value, onChange }) {
  const OPTIONS = [
    { key: 'firm', label: 'Firm' },
    { key: 'employee', label: 'Employee' },
  ];
  return (
    <div className="auth-typetoggle" role="tablist" aria-label="Account type">
      {OPTIONS.map((o) => (
        <button
          key={o.key}
          type="button"
          role="tab"
          aria-selected={value === o.key}
          className={value === o.key ? 'active' : ''}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Password field with a reveal toggle — shared by every auth form.
export function AuthPasswordInput(props) {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      {...props}
      type={visible ? 'text' : 'password'}
      iconRight={
        <button
          type="button"
          className="auth-eye"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {visible ? (
            <svg {...STROKE} width={18} height={18}>
              <path d="M3 3l18 18" />
              <path d="M10.6 5.1A9.8 9.8 0 0 1 12 5c5 0 8.6 3.9 10 7- .5 1.1-1.3 2.4-2.5 3.5M6.6 6.8C4.6 8.1 3 10 2 12c1.4 3.1 5 7 10 7 1.5 0 2.9-.3 4.1-.9" />
              <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
            </svg>
          ) : (
            <svg {...STROKE} width={18} height={18}>
              <path d="M2 12c1.4-3.1 5-7 10-7s8.6 3.9 10 7c-1.4 3.1-5 7-10 7S3.4 15.1 2 12z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      }
    />
  );
}

function AuthLayout({
  brandIcon,
  brandTitle,
  brandTagline,
  brandAccent, // optional short line under the headline
  features = [],
  stats = [],
  title,
  subtitle,
  error,
  footer,
  children,
}) {
  const reduceMotion = useReducedMotion();

  const rise = (delay) => ({
    initial: reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <div className="ds-root auth-shell">
      <aside className="auth-brand" aria-hidden="true">
        <div className="auth-mesh" />
        <div className="auth-grain" />

        <div className="auth-brand-top">
          <div className="auth-brand-mark">
            <span className="auth-brand-icon">{brandIcon}</span>
            <span className="auth-brand-name">{brandTitle}</span>
          </div>
        </div>

        <div className="auth-brand-inner">
          <motion.h2 className="auth-brand-headline" {...rise(0.05)}>
            {brandTagline}
          </motion.h2>
          {brandAccent && (
            <motion.p className="auth-brand-accentline" {...rise(0.12)}>
              {brandAccent}
            </motion.p>
          )}

          {/* Floating product vignette */}
          <motion.div className="auth-mock" {...rise(0.2)}>
            <div className="auth-mock-card auth-mock-main">
              <div className="auth-mock-row">
                <span className="auth-mock-kicker">GSTR-3B · Jun 2026</span>
                <span className="auth-mock-pill approved">Approved</span>
              </div>
              <div className="auth-mock-title">Acme Traders Pvt Ltd</div>
              <div className="auth-mock-meta">
                <span>Due 20 Jul</span>
                <span className="auth-mock-dot" />
                <span>Preparer · Neha</span>
                <span className="auth-mock-dot" />
                <span>Reviewer · Amit</span>
              </div>
              <div className="auth-mock-bar">
                <i style={{ width: '72%' }} />
              </div>
            </div>
            <div className="auth-mock-card auth-mock-side">
              <span className="auth-mock-kicker">TDS 24Q · Q1</span>
              <span className="auth-mock-pill review">In review</span>
            </div>
            <div className="auth-mock-card auth-mock-chip">
              <strong>7</strong> filings due this week
            </div>
          </motion.div>

          <motion.ul className="auth-brand-features" {...rise(0.3)}>
            {features.map((f) => (
              <li key={f.title}>
                <span className="auth-feature-icon">{FEATURE_ICONS[f.icon] || FEATURE_ICONS.tasks}</span>
                <span>
                  <strong>{f.title}</strong>
                  <em>{f.desc}</em>
                </span>
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="auth-brand-bottom">
          {stats.length > 0 && (
            <div className="auth-brand-stats">
              {stats.map((s) => (
                <div key={s.label} className="auth-stat">
                  <span className="auth-stat-value">{s.value}</span>
                  <span className="auth-stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          )}
          <p className="auth-brand-foot">© {new Date().getFullYear()} · Built for Indian CA firms</p>
        </div>
      </aside>

      <main className="auth-form-side">
        <motion.div
          className="auth-form-box"
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="auth-mobile-brand">
            <span className="auth-brand-icon">{brandIcon}</span>
            <span>{brandTitle}</span>
          </div>

          <h1 className="auth-title">{title}</h1>
          {subtitle && <p className="auth-subtitle">{subtitle}</p>}

          {error && (
            <motion.div
              className="auth-error"
              role="alert"
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15 }}
            >
              <svg {...STROKE} width={16} height={16}>
                <circle cx="12" cy="12" r="8.6" />
                <path d="M12 7.6v5" />
                <circle cx="12" cy="15.8" r="1" fill="currentColor" stroke="none" />
              </svg>
              {error}
            </motion.div>
          )}

          {children}

          {footer && <div className="auth-foot-links">{footer}</div>}
        </motion.div>
      </main>
    </div>
  );
}

export default AuthLayout;
